import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { FIELD_LABEL_CLASS, toneClasses, type BoardTone } from './airport-tokens';

// The PNR operation center's AIRPORT VISUAL LANGUAGE — the small set of marks
// that make the surface read like a departure board and a boarding pass instead
// of a generic web form. Everything in the drawer composes from these primitives
// so the dialect stays identical across the hero, the journey rail, the option
// list and the care cards:
//
//   FieldLabel  — the engraved micro caption over a value ("BOOKING REF").
//   Field       — that caption plus its value, boarding-pass style.
//   AirportCode — an IATA code at board size, monospaced.
//   Clock       — a departure time; tabular so digits never shift.
//   StatusLamp  — the FIDS indicator dot (pulses while something is live).
//   BoardChip   — the uppercase status tag off a departure board.
//
// The tone map (what each colour MEANS) lives in `./airport-tokens`.

/** The engraved micro caption printed over a value on a boarding pass. */
export function FieldLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('block', FIELD_LABEL_CLASS, className)}>{children}</span>;
}

/** A boarding-pass field: the engraved caption with its value beneath. */
export function Field({
  label,
  children,
  className,
  align = 'left',
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
  align?: 'left' | 'right';
}) {
  return (
    <div className={cn('min-w-0 space-y-1.5', align === 'right' && 'text-right', className)}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  );
}

const CODE_SIZE = {
  sm: 'text-base',
  md: 'text-2xl',
  lg: 'text-[2rem]',
} as const;

/** An IATA airport code at board size — mono, tight, unmistakable. */
export function AirportCode({
  code,
  size = 'md',
  className,
}: {
  code: string;
  size?: keyof typeof CODE_SIZE;
  className?: string;
}) {
  return (
    <span className={cn('font-mono font-bold leading-none tracking-tight', CODE_SIZE[size], className)}>
      {code}
    </span>
  );
}

/** A departure time. Tabular so the digits never shuffle as the clock ticks. */
export function Clock({
  children,
  className,
  ...rest
}: {
  children: ReactNode;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLSpanElement>, 'children' | 'className'>) {
  return (
    <span className={cn('font-semibold tabular-nums', className)} {...rest}>
      {children}
    </span>
  );
}

/** The FIDS indicator dot. `pulse` marks state that is still moving. */
export function StatusLamp({
  tone,
  pulse = false,
  className,
}: {
  tone: BoardTone;
  pulse?: boolean;
  className?: string;
}) {
  const solid = toneClasses(tone).solid;
  return (
    <span className={cn('relative flex h-2.5 w-2.5 shrink-0', className)} aria-hidden>
      {pulse ? (
        <span
          className={cn(
            'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 motion-reduce:hidden',
            solid,
          )}
        />
      ) : null}
      <span className={cn('relative inline-flex h-2.5 w-2.5 rounded-full', solid)} />
    </span>
  );
}

/** The uppercase status tag off a departure board. */
export function BoardChip({
  tone,
  children,
  className,
  ...rest
}: {
  tone: BoardTone;
  children: ReactNode;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLSpanElement>, 'children' | 'className'>) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-[0.08em] ring-1 ring-inset',
        toneClasses(tone).chip,
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
